"use client";

import { Text } from "@/components/ui/Text";
import type { useReactTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { ChevronsUpDown } from "lucide-react";
import React from "react";
import cn from "twsx";
import ColumnGroupHeaders from "./ColumnGroupHeaders";
import type { ColumnGroupHeaderLayout } from "./ColumnGroupHeaders";
import styles from "./List.module.css";
import type { HorizontalOverflowState, ListColumnMeta, StickyColumnLayout } from "./FilterListComponent";
import { getStickyColumnStyle, getStickyEdgeState, SelectionCheckbox } from "./FilterListRow";

/** The sticky column header row: select-all, sortable column labels and the Actions heading. */
export function FilterListHeader<$Row>({
  table,
  enableRowSelection,
  isAllSelected,
  onClearSelection,
  stickyColumnLayouts,
  horizontalOverflowState,
  showSelectionBoundary,
  columnHeaderGroups,
  groupedColumnHeaders,
  showActionsColumn
}: {
  table: ReturnType<typeof useReactTable<$Row>>;
  enableRowSelection: boolean;
  isAllSelected?: boolean;
  onClearSelection?: () => void;
  stickyColumnLayouts: Map<string, StickyColumnLayout>;
  horizontalOverflowState: HorizontalOverflowState;
  showSelectionBoundary: boolean;
  columnHeaderGroups?: Readonly<Record<string, string>>;
  groupedColumnHeaders: ColumnGroupHeaderLayout[];
  showActionsColumn: boolean;
}) {
  "use no memo";

  const hasHeaderSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected() || !!isAllSelected;
  const firstRightStickyHeaderId = table.getFlatHeaders().find(header => stickyColumnLayouts.get(header.id)?.side === "right")?.id;
  return <div data-list-table-header="true" data-column-groups={groupedColumnHeaders.length > 0 || undefined} className={cn("grid list-table-subgrid", styles.header)}>
      <ColumnGroupHeaders groups={groupedColumnHeaders} />
      {enableRowSelection && <div className={styles.selection} data-sticky-column="left" data-mask={horizontalOverflowState.hasScrolledLeft ? "left" : undefined} data-boundary={showSelectionBoundary ? "left" : undefined}>
          <span className={cn(styles.pinned, "flex items-center justify-center")}>
            <SelectionCheckbox ariaLabel={table.getIsAllRowsSelected() || !!isAllSelected ? "Clear selection" : "Select all loaded rows"} isIndeterminate={table.getIsSomePageRowsSelected() && !isAllSelected} checked={table.getIsAllRowsSelected() || !!isAllSelected} className={hasHeaderSelection ? undefined : "opacity-75"} onClick={() => {
          if (table.getIsAllRowsSelected() || isAllSelected) {
            onClearSelection?.();
            return;
          }
          table.toggleAllRowsSelected(true);
        }} />
          </span>
        </div>}

      {table.getFlatHeaders().map(header_0 => {
      const canSort = header_0.column.getCanSort();
      const sorted = header_0.column.getIsSorted();
      const stickyLayout = stickyColumnLayouts.get(header_0.id);
      const meta = header_0.column.columnDef.meta as ListColumnMeta | undefined;
      return <React.Fragment key={header_0.id}>
            {header_0.id === firstRightStickyHeaderId && <div aria-hidden="true" className="h-full" />}
            <div data-column-id={header_0.id} data-sticky-column={stickyLayout?.side} data-group-end={meta?.columnGroupEnd || undefined} {...getStickyEdgeState(stickyLayout, horizontalOverflowState)} className={cn(styles.cell, "gap-1", meta?.align === "right" && "justify-end text-right", stickyLayout?.side === "right" && "px-3", canSort && "cursor-pointer select-none")} style={getStickyColumnStyle(stickyLayout)} role="columnheader" aria-label={columnHeaderGroups?.[header_0.column.id] && typeof header_0.column.columnDef.header === "string" ? `${columnHeaderGroups[header_0.column.id]}: ${header_0.column.columnDef.header}` : undefined} aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : canSort ? "none" : undefined} tabIndex={canSort ? 0 : undefined} onClick={canSort ? header_0.column.getToggleSortingHandler() : undefined} onKeyDown={canSort ? event => {
          if (event.key !== "Enter" && event.key !== " ") {
            return;
          }
          event.preventDefault();
          header_0.column.toggleSorting();
        } : undefined}>
              <Text size="sm" weight="medium" tone="secondary" className={stickyLayout ? styles.pinned : undefined}>
                {flexRender(header_0.column.columnDef.header, header_0.getContext())}
              </Text>
              {canSort && (sorted ? <span aria-hidden="true" className={styles.sortCaret} data-direction={sorted} /> : <ChevronsUpDown aria-hidden="true" className="size-3 shrink-0 text-text-tertiary" />)}
            </div>
          </React.Fragment>;
    })}
      {showActionsColumn && <div data-column-id="Actions" data-sticky-column="right" data-boundary={horizontalOverflowState.hasScrollableRight ? "right" : undefined} className={cn(styles.actions, "text-xs font-medium text-text-secondary")} role="columnheader">
          Actions
        </div>}
    </div>;
}